import React, { useState } from "react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Play, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";

const BREVO_FORM_ACTION =
  "https://cafe0d23.sibforms.com/serve/MUIFABZ43SZXZJH3nshDiaEg6ITekEZFU8PddpHeCiP8S5uTb9c9TdmBgvBb6YOe4JWzk0GPU-rui_Fh58JPoW11WkmpCiZK3cfFJ1wm0t37eIN51lM70FU7cyI2ZSEPvDBFVNOY4rahnCk05XQEBHjxGtsfev6ojgvmcxsu6hEARNFV78urib3ojuu2WimeYvDMgahxoLNf1Cg9lQ==";

export default function DailyIdeaCard({ defaultEmail = "" }) {
  const [email, setEmail] = useState(defaultEmail);
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append("EMAIL", trimmed);
      body.append("email_address_check", "");
      body.append("locale", "it");
      // Brevo sibforms accepts cross-origin POSTs; the response is opaque in no-cors mode
      // but the submission is recorded. We optimistically show success.
      await fetch(BREVO_FORM_ACTION, {
        method: "POST",
        mode: "no-cors",
        body,
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      setSubscribed(true);
      toast.success("Iscrizione inviata — controlla la tua casella email ✉️");
    } catch (err) {
      toast.error("Invio non riuscito, riprova tra un attimo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="daily-idea-card"
      className="relative overflow-hidden rounded-3xl border border-[var(--mp-ink)] shadow-brutal-sm bg-[var(--mp-ink)] text-white"
    >
      <div
        aria-hidden
        className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[var(--mp-primary)] opacity-30 blur-3xl pointer-events-none"
      />
      <div
        aria-hidden
        className="absolute -bottom-24 -left-16 h-56 w-56 rounded-full bg-[var(--mp-secondary)] opacity-30 blur-3xl pointer-events-none"
      />

      <div className="relative p-8 sm:p-10 grid md:grid-cols-[1.3fr_1fr] gap-8 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--mp-yellow)] text-[var(--mp-ink)] text-[10px] font-bold uppercase tracking-[0.22em]">
            <Sparkles className="h-3.5 w-3.5" /> Servizio free
          </div>
          <h2 className="font-display mt-4 text-3xl sm:text-4xl tracking-tighter leading-[1.05]">
            Un'idea video <br />
            al giorno, <span className="text-[var(--mp-yellow)]">gratis</span>.
          </h2>
          <p className="mt-4 text-white/80 max-w-md text-[15px] leading-relaxed">
            Ogni mattina alle <strong>08:45</strong> ricevi via email un'idea video pronta da
            registrare nel tuo salone: titolo, hook, scaletta e caption. Pensata apposta per
            parrucchieri.
          </p>
          <ul className="mt-5 space-y-2 text-sm text-white/85">
            <li className="flex items-center gap-2">
              <Check className="h-4 w-4 text-[var(--mp-yellow)]" /> Idea video pronta
            </li>
            <li className="flex items-center gap-2">
              <Check className="h-4 w-4 text-[var(--mp-yellow)]" /> Hook iniziale forte
            </li>
            <li className="flex items-center gap-2">
              <Check className="h-4 w-4 text-[var(--mp-yellow)]" /> Scaletta + caption pronta
            </li>
          </ul>
        </div>

        <div className="rounded-2xl bg-white/[0.06] border border-white/15 backdrop-blur p-6">
          {subscribed ? (
            <div data-testid="daily-idea-success" className="text-center py-4">
              <div className="h-12 w-12 rounded-full bg-[var(--mp-yellow)] text-[var(--mp-ink)] grid place-items-center mx-auto">
                <Check className="h-6 w-6" strokeWidth={2.5} />
              </div>
              <div className="font-display text-xl mt-3">Sei iscritto ✦</div>
              <p className="mt-2 text-sm text-white/80">
                Controlla la tua casella email: da domani mattina ricevi la tua idea video ogni
                giorno alle 08:45.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} data-testid="daily-idea-form" className="space-y-4">
              <div>
                <label
                  htmlFor="daily-idea-email"
                  className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/70"
                >
                  La tua email
                </label>
                <Input
                  id="daily-idea-email"
                  data-testid="daily-idea-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nome@salone.it"
                  className="mt-2 h-12 rounded-xl bg-white/10 border-white/20 text-white placeholder:text-white/40 focus-visible:ring-2 focus-visible:ring-[var(--mp-yellow)] focus-visible:ring-offset-0 focus-visible:border-white/40"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="daily-idea-submit"
                className="w-full h-12 rounded-full bg-[var(--mp-yellow)] hover:bg-[#f5cf00] text-[var(--mp-ink)] font-semibold"
              >
                <Play className="h-4 w-4 mr-1" strokeWidth={2.5} />
                {submitting ? "Invio in corso…" : "Mandami l'idea video"}
              </Button>
              <p className="text-[11px] text-white/60 text-center">
                Gratis. Nessuno spam. Solo idee video utili per parrucchieri.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
